/**
 * @fileoverview Modal component for creating and editing Buzzer Battle themes ("De barrio en barrio").
 * Provides tabbed configuration for General, Cover, Game Arena, Mobile Fan view, and Players / Colors.
 * Supports Drag & Drop uploads with automatic SkiaSharp optimization via UploadMedia.
 * 
 * @module components/Admin/Games/BuzzerBattle/BuzzerBattleThemeModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { UploadMedia, SaveTheme } from './../../../../utilities/BL/BuzzerBattle';

/**
 * Reusable Drag & Drop Field component for individual media assets.
 * 
 * @function DragAndDropField
 * @param {Object} props - Component properties.
 * @param {string} props.fieldLabel - Text label displayed above the drop area.
 * @param {string} props.targetFieldName - Form property key being updated.
 * @param {string} props.mediaCategory - Category identifier for SkiaSharp optimization.
 * @param {string} props.currentValue - Current relative or absolute URL of the asset.
 * @param {Function} props.onUploadFile - Handler triggered when a file is dropped or selected.
 * @param {Function} props.resolveMediaUrl - Helper that resolves absolute URLs for preview.
 * @param {boolean} [props.isProcessing=false] - Whether an upload is in progress.
 * @param {string} [props.helpText] - Optional helper text below the dropzone.
 * @param {Function} [props.onClearFile] - Optional handler to clear the current asset.
 */
const DragAndDropField = ({
    fieldLabel,
    targetFieldName,
    mediaCategory,
    currentValue,
    onUploadFile,
    resolveMediaUrl,
    isProcessing = false,
    helpText = '',
    onClearFile = null
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = () => {
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        setIsDragHoverActive(false);
        const selectedFile = dropEvent.dataTransfer.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, mediaCategory, targetFieldName);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFile = changeEvent.target.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, mediaCategory, targetFieldName);
        }
    };

    return (
        <div className="mb-3">
            {fieldLabel && (
                <label className="form-label fw-bold text-dark mb-1">
                    {fieldLabel}:
                </label>
            )}
            <div
                className={`border border-2 border-dashed rounded-3 p-3 text-center cursor-pointer transition ${
                    isDragHoverActive ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-input-${targetFieldName}`).click()}
                style={{ minHeight: '90px', cursor: 'pointer' }}
            >
                {isProcessing ? (
                    <div className="py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span className="small text-primary fw-bold">Subiendo y optimizando...</span>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-primary fs-4 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && <small className="text-secondary d-block mt-1">{helpText}</small>}
                    </>
                )}
                <input
                    id={`file-input-${targetFieldName}`}
                    type="file"
                    className="d-none"
                    accept="image/*,video/mp4"
                    onChange={handleNativeFileChange}
                />
            </div>
            {currentValue && (
                <div className="mt-2 text-center bg-white p-2 rounded-3 border shadow-sm position-relative">
                    {onClearFile && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1 rounded-circle p-1"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClearFile(targetFieldName);
                            }}
                            title="Eliminar archivo"
                            style={{ width: '24px', height: '24px', lineHeight: '10px' }}
                        >
                            <i className="bi bi-x"></i>
                        </button>
                    )}
                    <img
                        src={resolveMediaUrl(currentValue)}
                        alt="Vista previa"
                        style={{ maxHeight: '75px', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(errorEvent) => {
                            errorEvent.target.style.display = 'none';
                        }}
                    />
                    <div className="small text-truncate text-muted mt-1" style={{ maxWidth: '100%' }}>
                        {currentValue}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Default player configuration templates for Buzzer Battle (Corner J1 vs Corner J2).
 */
const DEFAULT_PLAYERS_CONFIG = [
    {
        playerIndex: 0,
        name: 'Jugador 1',
        color: '#00e5ff',
        avatarUrl: '',
        winnerAvatarUrl: '',
        buzzerSoundUrl: '',
        keyCode: 'KeyA'
    },
    {
        playerIndex: 1,
        name: 'Jugador 2',
        color: '#ff0055',
        avatarUrl: '',
        winnerAvatarUrl: '',
        buzzerSoundUrl: '',
        keyCode: 'KeyL'
    }
];

/**
 * Buzzer Battle Theme Modal Component.
 * 
 * @function BuzzerBattleThemeModal
 * @param {Object} props - Component properties.
 * @param {boolean} props.isOpen - Controls modal visibility.
 * @param {Function} props.onCloseModal - Handler invoked to dismiss modal.
 * @param {Object} [props.initialThemeData=null] - Theme data when editing, or null for creating.
 * @param {Function} props.onThemeSavedSuccessfully - Callback invoked after successful save.
 * @returns {React.ReactElement|null} Modal markup.
 */
export const BuzzerBattleThemeModal = ({
    isOpen,
    onCloseModal,
    initialThemeData = null,
    onThemeSavedSuccessfully
}) => {
    const { showMessage } = useFloatMessage();
    const [activeTabKey, setActiveTabKey] = useState('general');
    const [isSavingInProgress, setIsSavingInProgress] = useState(false);
    const [activeUploadFieldKey, setActiveUploadFieldKey] = useState('');

    // Initial Theme State
    const defaultThemeStructure = {
        id: 0,
        name: '',
        brandLogoUrl: '',
        sponsorLogoUrl: '',
        coverTitle: 'DE BARRIO EN BARRIO',

        // Portada
        coverBackgroundType: 'image',
        coverBackgroundColor: '#0a0a14',
        coverBackgroundUrl: '',
        coverTextColor: '#ffffff',

        // Escenario de Juego
        gameBackgroundType: 'image',
        gameBackgroundColor: '#121224',
        gameBackgroundUrl: '',
        gameTextColor: '#ffffff',

        // Vista Móvil Aficionado
        fanBackgroundType: 'color',
        fanBackgroundColor: '#0d0e1a',
        fanBackgroundUrl: '',
        fanTextColor: '#ffffff',

        // Dinámica & Opciones
        showOptions: true,

        // Jugadores / Colores
        playersConfig: [...DEFAULT_PLAYERS_CONFIG],
        playersConfigJson: JSON.stringify(DEFAULT_PLAYERS_CONFIG),

        fontFamily: 'Bangers'
    };

    const [themeFormState, setThemeFormState] = useState(defaultThemeStructure);

    useEffect(() => {
        if (initialThemeData && (initialThemeData.id > 0 || initialThemeData.name)) {
            let parsedPlayers = DEFAULT_PLAYERS_CONFIG;

            if (Array.isArray(initialThemeData.playersConfig) && initialThemeData.playersConfig.length > 0) {
                parsedPlayers = initialThemeData.playersConfig;
            } else if (initialThemeData.playersConfigJson) {
                try {
                    const decoded = JSON.parse(initialThemeData.playersConfigJson);
                    if (Array.isArray(decoded) && decoded.length > 0) {
                        parsedPlayers = decoded;
                    }
                } catch {
                    parsedPlayers = DEFAULT_PLAYERS_CONFIG;
                }
            }

            setThemeFormState({
                ...defaultThemeStructure,
                ...initialThemeData,
                showOptions: initialThemeData.showOptions !== undefined ? Boolean(initialThemeData.showOptions) : true,
                playersConfig: parsedPlayers,
                playersConfigJson: JSON.stringify(parsedPlayers)
            });
        } else {
            setThemeFormState(defaultThemeStructure);
        }
        setActiveTabKey('general');
    }, [initialThemeData, isOpen]);

    if (!isOpen) return null;

    /**
     * Resolves absolute media URLs for rendering previews.
     */
    const resolveMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
    };

    /**
     * Updates an individual scalar property on the theme form state.
     */
    const handleFieldChange = (fieldName, fieldValue) => {
        setThemeFormState((previousState) => ({
            ...previousState,
            [fieldName]: fieldValue
        }));
    };

    /**
     * Updates an individual player attribute in the playersConfig array.
     */
    const handlePlayerConfigChange = (playerIndex, propertyKey, propertyValue) => {
        setThemeFormState((previousState) => {
            const updatedPlayers = [...previousState.playersConfig];
            if (!updatedPlayers[playerIndex]) {
                updatedPlayers[playerIndex] = { ...DEFAULT_PLAYERS_CONFIG[playerIndex] };
            }
            updatedPlayers[playerIndex] = {
                ...updatedPlayers[playerIndex],
                [propertyKey]: propertyValue
            };
            return {
                ...previousState,
                playersConfig: updatedPlayers,
                playersConfigJson: JSON.stringify(updatedPlayers)
            };
        });
    };

    /**
     * Handles single media upload for theme assets or player avatars.
     */
    const handleSingleFileUpload = async (fileObject, mediaCategory, targetFieldName) => {
        setActiveUploadFieldKey(targetFieldName);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Subiendo y optimizando archivo...' });

        try {
            const uploadResponse = await UploadMedia(fileObject, mediaCategory);
            if (uploadResponse.success && uploadResponse.url) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo cargado con éxito.' });

                // Check if updating a player avatar (format: player_{index}_{avatarKey})
                if (targetFieldName.startsWith('player_')) {
                    const parts = targetFieldName.split('_');
                    const pIndex = Number(parts[1]);
                    const pField = parts[2]; // 'avatarUrl' or 'winnerAvatarUrl'
                    handlePlayerConfigChange(pIndex, pField, uploadResponse.url);
                } else {
                    handleFieldChange(targetFieldName, uploadResponse.url);
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadResponse.message || 'Error al subir el archivo multimedia.'
                });
            }
        } catch (uploadException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado en la subida de archivo.' });
        } finally {
            setActiveUploadFieldKey('');
        }
    };

    /**
     * Clears a media field.
     */
    const handleClearFile = (targetFieldName) => {
        if (targetFieldName.startsWith('player_')) {
            const parts = targetFieldName.split('_');
            const pIndex = Number(parts[1]);
            const pField = parts[2];
            handlePlayerConfigChange(pIndex, pField, '');
        } else {
            handleFieldChange(targetFieldName, '');
        }
    };

    /**
     * Submits the theme form to be validated and saved on the backend.
     */
    const handleFormSubmit = async (formEvent) => {
        formEvent.preventDefault();

        if (!themeFormState.name || themeFormState.name.trim() === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Por favor asigna un nombre al tema.' });
            setActiveTabKey('general');
            return;
        }

        setIsSavingInProgress(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando configuración del tema...' });

        try {
            const payloadToSave = {
                ...themeFormState,
                playersConfigJson: JSON.stringify(themeFormState.playersConfig)
            };

            const saveResponse = await SaveTheme(payloadToSave);
            if (saveResponse.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Tema "${themeFormState.name}" guardado exitosamente.`
                });
                if (onThemeSavedSuccessfully) {
                    onThemeSavedSuccessfully(saveResponse.data);
                }
                onCloseModal();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: saveResponse.message || 'Error al guardar el tema.'
                });
            }
        } catch (saveException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al persistir el tema.' });
        } finally {
            setIsSavingInProgress(false);
        }
    };

    const player1Config = themeFormState.playersConfig[0] || DEFAULT_PLAYERS_CONFIG[0];
    const player2Config = themeFormState.playersConfig[1] || DEFAULT_PLAYERS_CONFIG[1];

    return (
        <>
            {/* Backdrop con zIndex: 99998 */}
            <div className="cModal__Background" style={{ zIndex: 99998 }} onClick={onCloseModal}></div>

            {/* Container con zIndex: 99999 */}
            <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '1000px', width: '95vw' }}>
                <div
                    className="cModal__Container__Content bg-white rounded-4 shadow-lg p-0 overflow-hidden text-dark position-relative"
                    style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* Modal Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-dark text-white border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-palette-fill text-warning fs-4"></i>
                            <h5 className="m-0 fw-bold">
                                {themeFormState.id > 0 ? `Editar Tema: ${themeFormState.name}` : 'Crear Nuevo Tema - De barrio en barrio'}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light rounded-circle"
                            onClick={onCloseModal}
                            title="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Navigation Tabs Header */}
                    <div className="bg-light border-bottom px-3 pt-2">
                        <ul className="nav nav-tabs border-0 flex-nowrap overflow-x-auto">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'general' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('general')}
                                >
                                    <i className="bi bi-sliders me-1"></i> 1. General
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'cover' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('cover')}
                                >
                                    <i className="bi bi-image me-1"></i> 2. Portada
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'game' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('game')}
                                >
                                    <i className="bi bi-tv me-1"></i> 3. Escenario de Juego
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'fan' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('fan')}
                                >
                                    <i className="bi bi-phone me-1"></i> 4. Vista Móvil
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link fw-bold px-3 py-2 ${activeTabKey === 'players' ? 'active text-primary border-bottom-0' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('players')}
                                >
                                    <i className="bi bi-people-fill me-1"></i> 5. Jugadores / Colores
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Scrollable Tab Body */}
                    <form onSubmit={handleFormSubmit} className="d-flex flex-column flex-grow-1 overflow-hidden m-0 p-0">
                        <div className="p-4 overflow-y-auto flex-grow-1" style={{ maxHeight: 'calc(90vh - 170px)' }}>
                            {/* ============================================================ */}
                            {/* TAB 1: GENERAL                                               */}
                            {/* ============================================================ */}
                            {activeTabKey === 'general' && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Nombre del Tema *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="ej. De barrio en barrio ADSL Clásico, Especial Aniversario..."
                                            value={themeFormState.name}
                                            onChange={(e) => handleFieldChange('name', e.target.value)}
                                            maxLength={100}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DragAndDropField
                                            fieldLabel="Logo de Marca / Club (Brand)"
                                            targetFieldName="brandLogoUrl"
                                            mediaCategory="logo"
                                            currentValue={themeFormState.brandLogoUrl}
                                            onUploadFile={handleSingleFileUpload}
                                            onClearFile={handleClearFile}
                                            resolveMediaUrl={resolveMediaUrl}
                                            isProcessing={activeUploadFieldKey === 'brandLogoUrl'}
                                            helpText="Logo del ADSL o equipo local (PNG transparente)"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <DragAndDropField
                                            fieldLabel="Logo del Patrocinador Oficial"
                                            targetFieldName="sponsorLogoUrl"
                                            mediaCategory="logo"
                                            currentValue={themeFormState.sponsorLogoUrl}
                                            onUploadFile={handleSingleFileUpload}
                                            onClearFile={handleClearFile}
                                            resolveMediaUrl={resolveMediaUrl}
                                            isProcessing={activeUploadFieldKey === 'sponsorLogoUrl'}
                                            helpText="Logo comercial o sponsor (PNG transparente)"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tipografía Principal</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Bangers"
                                            value={themeFormState.fontFamily || 'Bangers'}
                                            onChange={(e) => handleFieldChange('fontFamily', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold d-block">Visibilidad de Opciones</label>
                                        <div className="p-3 bg-light rounded-3 border d-flex align-items-center justify-content-between">
                                            <div>
                                                <strong>Mostrar opciones de respuesta en pantalla</strong>
                                                <small className="d-block text-muted">
                                                    Si está activado, la pantalla gigante proyectará las opciones (A, B, C, D) junto con la pregunta.
                                                </small>
                                            </div>
                                            <div className="form-check form-switch fs-4 ms-3 mb-0">
                                                <input
                                                    className="form-check-input cursor-pointer"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="switchShowOptions"
                                                    checked={themeFormState.showOptions}
                                                    onChange={(e) => handleFieldChange('showOptions', e.target.checked)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 2: PORTADA                                               */}
                            {/* ============================================================ */}
                            {activeTabKey === 'cover' && (
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Título de Portada</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="DE BARRIO EN BARRIO"
                                            value={themeFormState.coverTitle || ''}
                                            onChange={(e) => handleFieldChange('coverTitle', e.target.value)}
                                            maxLength={150}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Fuente de Portada</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.coverTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.coverTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Tipo de Fondo de Portada</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.coverBackgroundType || 'image'}
                                            onChange={(e) => handleFieldChange('coverBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen / Textura</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-bold">Color de Fondo de Portada</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.coverBackgroundColor || '#0a0a14'}
                                                onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.coverBackgroundColor || '#0a0a14'}
                                                onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.coverBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo para Portada (1920x1080)"
                                                targetFieldName="coverBackgroundUrl"
                                                mediaCategory="background"
                                                currentValue={themeFormState.coverBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                onClearFile={handleClearFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'coverBackgroundUrl'}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 3: ESCENARIO DE JUEGO                                    */}
                            {/* ============================================================ */}
                            {activeTabKey === 'game' && (
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo de Juego</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.gameBackgroundType || 'image'}
                                            onChange={(e) => handleFieldChange('gameBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen de Estadio / Ring</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo de Juego</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.gameBackgroundColor || '#121224'}
                                                onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.gameBackgroundColor || '#121224'}
                                                onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto en Pantalla</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.gameTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.gameTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.gameBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo de Combate (1920x1080)"
                                                targetFieldName="gameBackgroundUrl"
                                                mediaCategory="background"
                                                currentValue={themeFormState.gameBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                onClearFile={handleClearFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'gameBackgroundUrl'}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 4: VISTA MÓVIL                                           */}
                            {/* ============================================================ */}
                            {activeTabKey === 'fan' && (
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Tipo de Fondo Móvil</label>
                                        <select
                                            className="form-select"
                                            value={themeFormState.fanBackgroundType || 'color'}
                                            onChange={(e) => handleFieldChange('fanBackgroundType', e.target.value)}
                                        >
                                            <option value="color">Color Sólido</option>
                                            <option value="image">Imagen Vertical / Patrón</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Fondo Móvil</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.fanBackgroundColor || '#0d0e1a'}
                                                onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.fanBackgroundColor || '#0d0e1a'}
                                                onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-bold">Color de Texto Móvil</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="color"
                                                className="form-control form-control-color"
                                                value={themeFormState.fanTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                className="form-control text-uppercase font-monospace"
                                                value={themeFormState.fanTextColor || '#ffffff'}
                                                onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {themeFormState.fanBackgroundType === 'image' && (
                                        <div className="col-12">
                                            <DragAndDropField
                                                fieldLabel="Imagen de Fondo Móvil (1080x1920 Vertical)"
                                                targetFieldName="fanBackgroundUrl"
                                                mediaCategory="background"
                                                currentValue={themeFormState.fanBackgroundUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                onClearFile={handleClearFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'fanBackgroundUrl'}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============================================================ */}
                            {/* TAB 5: JUGADORES / COLORES                                   */}
                            {/* ============================================================ */}
                            {activeTabKey === 'players' && (
                                <div className="row g-4">
                                    {/* JUGADOR 1 */}
                                    <div className="col-md-6 border-end pe-md-3">
                                        <div className="p-3 bg-light rounded-4 border shadow-sm h-100">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h6 className="fw-bold m-0" style={{ color: player1Config.color || '#00e5ff' }}>
                                                    <i className="bi bi-person-bounding-box me-1"></i> Jugador 1 (Lado Izquierdo)
                                                </h6>
                                                <span className="badge bg-dark">Tecla Buzzer: {player1Config.keyCode || 'KeyA'}</span>
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label fw-bold small text-muted">Nombre del Jugador 1:</label>
                                                <input
                                                    type="text"
                                                    className="form-control fw-bold"
                                                    value={player1Config.name || ''}
                                                    onChange={(e) => handlePlayerConfigChange(0, 'name', e.target.value)}
                                                    placeholder="Jugador 1"
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label fw-bold small text-muted">Color Primario J1:</label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={player1Config.color || '#00e5ff'}
                                                        onChange={(e) => handlePlayerConfigChange(0, 'color', e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control text-uppercase font-monospace"
                                                        value={player1Config.color || '#00e5ff'}
                                                        onChange={(e) => handlePlayerConfigChange(0, 'color', e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <DragAndDropField
                                                fieldLabel="Avatar de Perfil J1"
                                                targetFieldName="player_0_avatarUrl"
                                                mediaCategory="avatar"
                                                currentValue={player1Config.avatarUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                onClearFile={handleClearFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player_0_avatarUrl'}
                                                helpText="Avatar o foto de perfil (PNG transparente 500x500)"
                                            />

                                            <DragAndDropField
                                                fieldLabel="Pose de Victoria J1"
                                                targetFieldName="player_0_winnerAvatarUrl"
                                                mediaCategory="avatar"
                                                currentValue={player1Config.winnerAvatarUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                onClearFile={handleClearFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player_0_winnerAvatarUrl'}
                                                helpText="Pose ganadora para el podio final"
                                            />
                                        </div>
                                    </div>

                                    {/* JUGADOR 2 */}
                                    <div className="col-md-6 ps-md-3">
                                        <div className="p-3 bg-light rounded-4 border shadow-sm h-100">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h6 className="fw-bold m-0" style={{ color: player2Config.color || '#ff0055' }}>
                                                    <i className="bi bi-person-bounding-box me-1"></i> Jugador 2 (Lado Derecho)
                                                </h6>
                                                <span className="badge bg-dark">Tecla Buzzer: {player2Config.keyCode || 'KeyL'}</span>
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label fw-bold small text-muted">Nombre del Jugador 2:</label>
                                                <input
                                                    type="text"
                                                    className="form-control fw-bold"
                                                    value={player2Config.name || ''}
                                                    onChange={(e) => handlePlayerConfigChange(1, 'name', e.target.value)}
                                                    placeholder="Jugador 2"
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label fw-bold small text-muted">Color Primario J2:</label>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={player2Config.color || '#ff0055'}
                                                        onChange={(e) => handlePlayerConfigChange(1, 'color', e.target.value)}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control text-uppercase font-monospace"
                                                        value={player2Config.color || '#ff0055'}
                                                        onChange={(e) => handlePlayerConfigChange(1, 'color', e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <DragAndDropField
                                                fieldLabel="Avatar de Perfil J2"
                                                targetFieldName="player_1_avatarUrl"
                                                mediaCategory="avatar"
                                                currentValue={player2Config.avatarUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                onClearFile={handleClearFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player_1_avatarUrl'}
                                                helpText="Avatar o foto de perfil (PNG transparente 500x500)"
                                            />

                                            <DragAndDropField
                                                fieldLabel="Pose de Victoria J2"
                                                targetFieldName="player_1_winnerAvatarUrl"
                                                mediaCategory="avatar"
                                                currentValue={player2Config.winnerAvatarUrl}
                                                onUploadFile={handleSingleFileUpload}
                                                onClearFile={handleClearFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'player_1_winnerAvatarUrl'}
                                                helpText="Pose ganadora para el podio final"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-3 bg-light border-top d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold px-4"
                                onClick={onCloseModal}
                                disabled={isSavingInProgress}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-success fw-bold px-4"
                                disabled={isSavingInProgress}
                            >
                                {isSavingInProgress ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill me-1"></i> Guardar Tema
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};
